// @flow

// Libs
import React, { Component } from 'react'
import PropTypes from 'prop-types'

// Utils
import nAf from 'fc-utils/nAf'

// Components
import Table from './Table'
import TableHeader from './TableHeader'
import TableFilters from './TableFilters'
import TableBar from './TableBar'
import TableBody from './TableBody'
import TableFooter from './TableFooter'
import DefaultTranslated from './Translated'

// Constants
import { LISTVIEW } from './constants'
import {
  SIZE_CONFIG,
  SMALL_SIZE,
  MEDIUM_SIZE,
  LARGE_SIZE,
  AVAILABLE_SIZES_ARR,
} from 'fc-component-datatable'

// Types
import {
  renderer as rendererPropType,
  selectedRecords as selectedRecordsPropTypes,
  columns as columnsPropTypes,
  mode as modePropType,
} from './propTypes'

// Styles
import * as defaultStyles from './styles'

// Local constants
const RECORDS_PER_PAGE = 15

// Exports
export default function createControlledComponent ({

  // PropType describing a single record
  recordPropType = PropTypes.object,
  // Default permissions
  allowGrouping = true,
  allowSelecting = true,
  allowSorting = true,
  allowFiltering = true,
  allowPagination = true,
  allowSelectAll = true,

  defaultPrinter = {},
  defaultRenderer = {},
  defaultColumns = [],

  defaultWrapperStyle = defaultStyles.defaultWrapperStyle,
  defaultTableStyle = defaultStyles.defaultTableStyle,
  rowStyle,
} = {}) {
  class ControlledDataTable extends Component {
    componentDidMount () {
      const { routerQuery, canPaginate, onPaginateLast, pages } = this.props
      const hasPages = pages.length > 0

      if (canPaginate && hasPages && routerQuery === '?paginate=last') {
        onPaginateLast()
      }
    }

    componentWillUnmount () {
      this.props.actions.resetDataTableState()
    }

    render () {
      const { canSelect, selectedRecords, records, columns } = this.props
      const selectedIds = (canSelect && selectedRecords) || []
      const allSelected = canSelect && records.every(({id}) => selectedRecords.indexOf(id) >= 0) || false
      const columnsNumberPerSize = {
        [SMALL_SIZE]: 0,
        [MEDIUM_SIZE]: 0,
        [LARGE_SIZE]: 0,
      }

      columns.forEach((column) => {
        const size = AVAILABLE_SIZES_ARR.indexOf(column.size) > -1 ? column.size : MEDIUM_SIZE

        columnsNumberPerSize[size]++
      })

      const numberOfFractions = AVAILABLE_SIZES_ARR.reduce((prev, curSize) =>
        prev + columnsNumberPerSize[curSize] * SIZE_CONFIG[curSize]
        , 0)
      const base = 100 / numberOfFractions
      const updatedColumns = columns.map((column) => {
        const size = AVAILABLE_SIZES_ARR.indexOf(column.size) > -1 ? column.size : MEDIUM_SIZE
        const coef = SIZE_CONFIG[size]
        const width = coef * base

        return {
          width,
          ...column,
        }
      })

      return (
        <div style={this.props.style}>
          <Table style={this.props.tableStyle}>
            <TableHeader
              columns={updatedColumns}
              groupedBy={this.props.groupBy}
              sortedBy={this.props.sortBy}
              asc={this.props.asc}
              canSort={this.props.canSort}
              canGroup={this.props.canGroup}
              onSort={this.props.onSort}
              selectedRecords={selectedIds}
              allSelected={allSelected}
              onSelectAll={this.props.onSelectAll}
              canSelectAll={this.props.canSelect && this.props.canSelectAll}
              hideSelectAll={this.props.hideSelectAll}
              hideSorters={this.props.hideSorters}
            />

            <TableFilters
              columns={updatedColumns}
              onFilter={this.props.onFilter}
              filters={this.props.filters}
            />

            <TableBar
              selectedRecords={selectedIds}
              hideSelectAll={this.props.hideSelectAll}
              onClearSelection={this.props.onClearSelection}
              filters={this.props.filters}
              onFilter={this.props.onFilter}
              Bar={this.props.Bar}
            >
              {this.props.tools}
            </TableBar>

            <TableBody
              records={records}
              columns={updatedColumns}
              renderer={this.props.renderer}
              onClick={this.props.onClickRow}
              onSelect={this.props.canSelect && this.props.onSelect}
              selectedRecords={selectedIds}
              rowStyle={this.props.tableRowStyle}
            />

            {this.props.canPaginate && this.props.mode === LISTVIEW ? <TableFooter
              page={this.props.page}
              pages={this.props.pages}
              perPage={this.props.perPage}
              start={this.props.start}
              end={this.props.end}
              max={this.props.max}
              onPaginateLeft={this.props.onPaginateLeft}
              onPaginateRight={this.props.onPaginateRight}
              onChangePerPage={this.props.onChangePerPage}
              onPaginateFirst={this.props.onPaginateFirst}
              onPaginateLast={this.props.onPaginateLast}
            /> : null }
          </Table>
        </div>
      )
    }
  }

  ControlledDataTable.propTypes = {
    records: PropTypes.any,
    columns: PropTypes.any,
    mobileColumns: PropTypes.any,
    mode: modePropType,
    page: PropTypes.number,
    perPage: PropTypes.number,
    max: PropTypes.number,
    start: PropTypes.number,
    end: PropTypes.number,
    pages: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.object)),
    defaultColumn: PropTypes.oneOfType([
      PropTypes.string,
      columnsPropTypes,
    ]),
    selectedRecords: selectedRecordsPropTypes,

    groupBy: PropTypes.string,
    sortBy: PropTypes.string,
    asc: PropTypes.bool,
    filters: PropTypes.object,

    canGroup: PropTypes.bool,
    canSort: PropTypes.bool,
    canFilter: PropTypes.bool,
    canPaginate: PropTypes.bool,
    canSelect: PropTypes.bool,
    canSelectAll: PropTypes.bool,
    hideSelectAll: PropTypes.bool,
    hideSorters: PropTypes.bool,

    renderer: rendererPropType,
    tools: PropTypes.arrayOf(PropTypes.node),
    Bar: PropTypes.func,
    printer: PropTypes.object,
    viewport: PropTypes.object,

    style: PropTypes.object,
    tableStyle: PropTypes.object,
    tableRowStyle: PropTypes.object,

    onPaginateLeft: PropTypes.func,
    onPaginateRight: PropTypes.func,
    onPaginateFirst: PropTypes.func,
    onPaginateLast: PropTypes.func,
    onChangePerPage: PropTypes.func,
    onSelect: PropTypes.func,
    onSort: PropTypes.func,
    onFilter: PropTypes.func,
    onSelectAll: PropTypes.func,
    onClearSelection: PropTypes.func,
    onClickRow: PropTypes.func,
  }

  ControlledDataTable.defaultProps = {
    records: [],
    columns: defaultColumns,
    mode: LISTVIEW,
    page: 0,
    perPage: RECORDS_PER_PAGE,
    max: 0,
    start: 0,
    end: RECORDS_PER_PAGE,
    pages: [0],
    selectedRecords: [],

    groupBy: null,
    sortBy: null,
    asc: true,
    filters: {},

    canGroup: allowGrouping,
    canSort: allowSorting,
    canFilter: allowFiltering,
    canPaginate: allowPagination,
    canSelect: allowSelecting,
    canSelectAll: allowSelectAll,
    hideSelectAll: false,
    hideSorters: false,

    renderer: defaultRenderer,
    tools: [],
    Translated: DefaultTranslated,
    printer: defaultPrinter,
    viewport: {
      width: 800
    },

    style: defaultWrapperStyle,
    tableStyle: defaultTableStyle,
    tableRowStyle: rowStyle,

    onPaginateLeft: nAf,
    onPaginateRight: nAf,
    onPaginateFirst: nAf,
    onPaginateLast: nAf,
    onChangePerPage: nAf,
    onSelect: nAf,
    onSort: nAf,
    onFilter: nAf,
    onSelectAll: nAf,
    onClearSelection: nAf,
    onClickRow: nAf,
}

  return ControlledDataTable
}
